// Step 3 of the campaign builder: one card per campaign audience.

import { MetaDeliveryPolicy } from '@/components/meta-delivery-policy';
import { RequiredField } from '@/components/required-field';
import { defaultGroupsForType } from './audience';
import { LockBanner } from '@/components/lock-primitives';
import { Icons } from '@/components/primitives';
import { Section } from '@/components/wizard-section';
import { allAudienceSlotsConfirmed } from '@/lib/campaign-audience-slots';
import { CampaignAudienceChannelNotice, CampaignAudienceSlotCard } from './create-audience-slot';
import type { CampaignAudienceSlot } from '@/lib/campaign-audience-slots';
import type { CreateFlowModel } from './create-flow-model';

export function AudienceStep({ model }: { model: CreateFlowModel }) {
  const { productionRows, availableAudiences, wasLive, production,
    channel, metaContract, audienceSlots, setAudienceSlots, unresolvedSegmentPositions,
    slotConfirmed, setSlotConfirmed, patchSlot, audConfirmed, required, open, setOpen, toggle,
    audSize, slotAllocations, audSizeUnavailableReason, slotSizeSummaries, handleSlotPreviewChange,
    handleDuplicate,
  } = model;
  return (
    <Section n="3" title="Audience" summary={audConfirmed ? (typeof audSize === 'number' ? `Confirmed · ${audSize.toLocaleString()} matched` : 'Confirmed · model size pending') : (production ? 'Configure who sees this campaign.' : 'Configure who sees this campaign.')} open={open.aud} done={audConfirmed} onToggle={()=>toggle('aud')} locked={wasLive} lockNotice={
      <LockBanner className="locked-banner--section" onDuplicate={() => { void handleDuplicate(); }}>
        Audience definitions can't be changed after the publishing handoff because the campaign pins exact audience revisions. Duplicate to change the audience plan.
      </LockBanner>
    }>
      <MetaDeliveryPolicy contract={metaContract} scopes={['audience']} />
      <CampaignAudienceChannelNotice
        channel={channel}
        audienceTypes={audienceSlots.map((slot) => slot.type)}
      />
      {unresolvedSegmentPositions.length > 0 && (
        <div className="facet" role="alert" style={{marginBottom:12,borderColor:'var(--bad)',color:'var(--bad)'}}>
          {unresolvedSegmentPositions.length} pinned audience definition
          {unresolvedSegmentPositions.length === 1 ? '' : 's'} could not be read
          (position {unresolvedSegmentPositions.join(', ')}). Saving would drop
          {unresolvedSegmentPositions.length === 1 ? ' it' : ' them'} and orphan the
          delivery group{unresolvedSegmentPositions.length === 1 ? '' : 's'} already
          published. Duplicate the campaign instead.
        </div>
      )}
      <RequiredField
        fieldKey="campaign-audience"
        message={required.messages['campaign-audience']}
        attempt={required.attempt}
        surface
        aria-disabled={wasLive}
        style={wasLive ? {pointerEvents:'none',opacity:.82} : undefined}
      >
        <div>
          {audienceSlots.map((slot, index) => (
            <CampaignAudienceSlotCard
              key={slot.id}
              slot={slot}
              index={index}
              total={audienceSlots.length}
              allocationBps={slotAllocations[index] ?? 0}
              production={production}
              audiences={availableAudiences}
              productions={productionRows}
              locked={wasLive}
              onPatch={(id, patch) => {
                required.clear('campaign-audience');
                patchSlot(id, patch);
              }}
              onRemove={audienceSlots.length > 1 && !wasLive
                ? () => {
                  required.clear('campaign-audience');
                  setAudienceSlots((current) => current.filter((entry) => entry.id !== slot.id));
                }
                : null}
              onPreview={handleSlotPreviewChange}
              confirmed={!!slotConfirmed[slot.id]}
              onConfirm={() => {
                required.clear('campaign-audience');
                const next = { ...slotConfirmed, [slot.id]: true };
                setSlotConfirmed(next);
                // Advance only when the LAST outstanding slot is confirmed.
                //
                // 2843eaf turned the single-audience block into per-slot
                // cards and dropped this line, making Audience the only
                // section in the wizard with no way out. It also hid the
                // confirmation entirely: `Section` renders the tick and the
                // summary behind `!open`, and the Confirm buttons live
                // inside `{open && …}`, so a section that never closes can
                // never show that it is done however `audConfirmed`
                // evaluates. One missing line produced both symptoms.
                //
                // `next` is built explicitly rather than calling setOpen
                // inside a setSlotConfirmed updater: StrictMode is on, and
                // an impure updater would double-fire.
                if (allAudienceSlotsConfirmed(audienceSlots, next)) {
                  setOpen((current) => ({ ...current, aud: false, cr: true }));
                }
              }}
              onEdit={() => setSlotConfirmed((current) => ({ ...current, [slot.id]: false }))}
            />
          ))}
          {!wasLive && (() => {
            const candidate: CampaignAudienceSlot = {
              id: `segment-${Date.now()}`,
              type: 'Direct',
              groups: defaultGroupsForType('Direct', {
                audiences: availableAudiences,
                productions: productionRows,
              }),
            };
            return (
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <button
                  type="button"
                  className="btn btn-sm"
                  title="Add another audience to this campaign"
                  onClick={() => {
                    required.clear('campaign-audience');
                    setAudienceSlots((current) => [...current, candidate]);
                    setSlotConfirmed((current) => ({ ...current, [candidate.id]: false }));
                  }}
                >
                  {Icons.plus} Add audience
                </button>
                <span className="muted" style={{fontSize:11}}>
                  Each audience becomes its own ad set and its own reportable delivery
                  group. Budget splits equally between them.
                </span>
              </div>
            );
          })()}
          {audienceSlots.length > 1 && (
            <div className="facet" style={{marginTop:12,background:'var(--bg-sunk)'}}>
              <div className="facet-hd"><h5>Sizes</h5></div>
              <div style={{display:'flex',flexDirection:'column',gap:4}}>
                {slotSizeSummaries.map((entry) => (
                  <div key={entry.id} style={{fontSize:11.5}}>
                    <b style={{color:'var(--ink)'}}>{entry.label}</b>
                    <span className="muted"> · {entry.type} · {entry.size !== null
                      ? `${entry.size.toLocaleString()} ${entry.prospecting ? 'modelled' : 'matched'}`
                      : 'size pending'}</span>
                  </div>
                ))}
                <div className="muted" style={{fontSize:11,marginTop:2}}>
                  {audSizeUnavailableReason
                    ? `Campaign total — ${audSizeUnavailableReason}.`
                    : audSize !== null
                      ? `Campaign total · ${audSize.toLocaleString()} matched, deduplicated across audiences.`
                      : 'Campaign total · pending.'}
                </div>
              </div>
            </div>
          )}
        </div>
      </RequiredField>
    </Section>
  );
}
